<script lang="ts">
// this must be exported here for the keep alive on reroute
export default {
  name: "Roles",
};
</script>

<script setup lang="ts">
import RoleTable from "@/components/models/role/RoleTable.vue";
import { useSnackStore } from "@/stores/snack";
import { useQuery } from "@tanstack/vue-query";
import { definePage } from "unplugin-vue-router/runtime";
import { onMounted, provide, ref } from "vue";

import RoleService, { type TRole } from "@/api/role";
import { ActionsEnum, SubjectsEnum, type AppAbility } from "@/services/ability";
import { generateFormOptions, type PaginatedType, type VFormOptions } from "@/utils/DjangoTypes";
import { useAbility } from "@casl/vue";
import { DefaultServerTableParams, type ServerTableParamsType } from "@/utils/TableUtils";

definePage({
  meta: {
    title: "Roles",
    checkPrivileges: () => {
      const { can } = useAbility<AppAbility>();
      return can(ActionsEnum.USE, SubjectsEnum.ROLE);
    },
  },
});

const snackStore = useSnackStore();
const roleFetchEnabled = ref(false);

const roleOptions = ref<VFormOptions>({});

const queriesTableParams: Record<string, ServerTableParamsType> = {
  roles: DefaultServerTableParams,
};
provide("queriesTableParams", queriesTableParams);

const {
  data: roles,
  isPending: isPending,
  isFetching: isFetching,
} = useQuery({
  queryKey: ["roles"],
  queryFn: async (): Promise<PaginatedType<TRole[]> | undefined> => {
    try {
      const [result, restOptions] = await Promise.all([
        RoleService.role.getAll(queriesTableParams.roles),
        RoleService.role.getOptions(),
      ]);

      roleOptions.value = generateFormOptions(restOptions.data.actions.POST);

      return result.data;
    } catch (error) {
      snackStore.showError(error);
    }
  },
  enabled: () => roleFetchEnabled.value,
});

onMounted(async () => {
  roleFetchEnabled.value = true;
});
</script>

<template>
  <v-container fluid>
    <div class="tw:!mb-10">
      <role-table
        :query-key="'roles'"
        :data="roles"
        :loading="isPending || isFetching"
        :options="roleOptions"
      />
    </div>
  </v-container>
</template>

<style scoped></style>
